import { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import "./GuidedTour.css";

// ─── ICONS ─────────────────────────────────────────────────────
const TIcon = {
  Close: () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
};

// ─── PERSISTENCE HELPERS ───────────────────────────────────────
const isTourDone = (key) => {
  if (!key) return false;
  try { return localStorage.getItem(key) === "1"; } catch (err) { return false; }
};
const markTourDone = (key) => {
  if (!key) return;
  try { localStorage.setItem(key, "1"); } catch (err) { /* noop */ }
};
// Exportado por si quieres forzar que el tour vuelva a mostrarse automáticamente.
export const resetGuidedTour = (key) => {
  try { localStorage.removeItem(key); } catch (err) { /* noop */ }
};

// ─── LAYOUT CONSTANTS ──────────────────────────────────────────
const VIEWPORT_PADDING = 12;
const TOOLTIP_GAP = 14;
const DEFAULT_SPOTLIGHT_PADDING = 8;
const DEFAULT_WAIT_TIMEOUT = 1600;
const DEFAULT_TOOLTIP_SIZE = { width: 328, height: 180 };

// ─── DOM HELPERS ───────────────────────────────────────────────
const safeQuery = (selector) => {
  if (!selector) return null;
  try { return document.querySelector(selector); } catch (err) { return null; }
};

const measureTarget = (selector, padding) => {
  const el = safeQuery(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  const pad = padding ?? DEFAULT_SPOTLIGHT_PADDING;
  return {
    top: r.top - pad,
    left: r.left - pad,
    width: r.width + pad * 2,
    height: r.height + pad * 2,
  };
};

// ¿El objetivo necesita que hagamos scroll para verlo? Si ya está razonablemente
// visible en la ventana, NO scrolleamos (evita saltos innecesarios, p.ej. en la grilla).
const needsScroll = (selector) => {
  const el = safeQuery(selector);
  if (!el) return false;
  const r = el.getBoundingClientRect();
  const vh = window.innerHeight;
  const vw = window.innerWidth;
  const margin = 8;
  // Fuera de la ventana en horizontal o vertical → sí scrollear.
  if (r.right <= margin || r.left >= vw - margin) return true;
  if (r.bottom <= margin || r.top >= vh - margin) return true;
  // Elemento más alto que la ventana (p.ej. grilla larga): ya ocupa la vista, no scrollear.
  if (r.height >= vh - margin * 2) return false;
  // Cabe pero está cortado arriba o abajo → scrollear lo mínimo.
  if (r.top < margin || r.bottom > vh - margin) return true;
  return false;
};

const computeTooltip = (rect, placement, size) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const tw = size.width;
  const th = size.height;
  const pad = VIEWPORT_PADDING;

  // Sin objetivo → globo centrado, sin flecha.
  if (!rect) {
    return {
      top: Math.max(pad, (vh - th) / 2),
      left: Math.max(pad, (vw - tw) / 2),
      placement: "center",
      arrow: null,
    };
  }

  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const space = {
    top: rect.top,
    bottom: vh - (rect.top + rect.height),
    left: rect.left,
    right: vw - (rect.left + rect.width),
  };

  let place = placement || "auto";
  if (place === "auto") {
    if (space.bottom >= th + TOOLTIP_GAP + pad) place = "bottom";
    else if (space.top >= th + TOOLTIP_GAP + pad) place = "top";
    else if (space.right >= tw + TOOLTIP_GAP + pad) place = "right";
    else if (space.left >= tw + TOOLTIP_GAP + pad) place = "left";
    else place = "bottom";
  }
  // Si el lado pedido no cabe, lo volteamos automáticamente.
  if (place === "left" && space.left < tw + TOOLTIP_GAP + pad && space.right >= tw + TOOLTIP_GAP + pad) place = "right";
  if (place === "right" && space.right < tw + TOOLTIP_GAP + pad && space.left >= tw + TOOLTIP_GAP + pad) place = "left";
  if (place === "top" && space.top < th + TOOLTIP_GAP + pad && space.bottom >= th + TOOLTIP_GAP + pad) place = "bottom";
  if (place === "bottom" && space.bottom < th + TOOLTIP_GAP + pad && space.top >= th + TOOLTIP_GAP + pad) place = "top";

  let top;
  let left;
  if (place === "bottom") { top = rect.top + rect.height + TOOLTIP_GAP; left = cx - tw / 2; }
  else if (place === "top") { top = rect.top - th - TOOLTIP_GAP; left = cx - tw / 2; }
  else if (place === "right") { left = rect.left + rect.width + TOOLTIP_GAP; top = cy - th / 2; }
  else { left = rect.left - tw - TOOLTIP_GAP; top = cy - th / 2; }

  const clampedLeft = Math.min(Math.max(left, pad), Math.max(pad, vw - tw - pad));
  const clampedTop = Math.min(Math.max(top, pad), Math.max(pad, vh - th - pad));

  let arrow = null;
  if (place === "bottom" || place === "top") {
    arrow = { side: place, x: Math.min(Math.max(cx - clampedLeft, 20), tw - 20) };
  } else {
    arrow = { side: place, y: Math.min(Math.max(cy - clampedTop, 20), th - 20) };
  }

  return { top: clampedTop, left: clampedLeft, placement: place, arrow };
};

const arrowStyle = (arrow) => {
  if (!arrow) return {};
  if (arrow.side === "top" || arrow.side === "bottom") return { left: arrow.x };
  return { top: arrow.y };
};

// ─── COMPONENT ─────────────────────────────────────────────────
export default function GuidedTour({
  steps = [],
  storageKey,
  autoStart = true,
  startSignal,
  onFinish,
}) {
  const [running, setRunning] = useState(false);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [pos, setPos] = useState({ top: 0, left: 0, placement: "center", arrow: null });
  const tooltipRef = useRef(null);
  const rafRef = useRef(0);
  const dirRef = useRef(1); // dirección de navegación (para saltar pasos sin objetivo)

  const total = steps.length;
  const step = steps[index] || null;

  const readTooltipSize = () => (
    tooltipRef.current
      ? { width: tooltipRef.current.offsetWidth, height: tooltipRef.current.offsetHeight }
      : DEFAULT_TOOLTIP_SIZE
  );

  const start = useCallback((force = false) => {
    if (!steps.length) return;
    if (!force && isTourDone(storageKey)) return;
    // Marca el tour como visto apenas arranca automáticamente, así solo sale
    // la primera vez aunque se cierre, se salte o se recargue a mitad.
    if (!force) markTourDone(storageKey);
    dirRef.current = 1;
    setIndex(0);
    setRunning(true);
  }, [steps.length, storageKey]);

  // Arranque automático (respeta el progreso guardado).
  useEffect(() => {
    if (!autoStart) return undefined;
    const timer = setTimeout(() => start(false), 400);
    return () => clearTimeout(timer);
  }, [autoStart, start]);

  // Arranque manual (botón "ver recorrido"): ignora el progreso guardado.
  const firstSignal = useRef(true);
  useEffect(() => {
    if (firstSignal.current) { firstSignal.current = false; return; }
    if (startSignal !== undefined && startSignal !== null) start(true);
  }, [startSignal, start]);

  // ¿El paso es alcanzable? Los pasos con acción siempre se intentan -su propia
  // lógica decide si continúa o se salta-; los que solo tienen objetivo se saltan
  // si el objetivo no existe todavía.
  const resolveIndex = useCallback((from, dir) => {
    let i = from;
    while (i >= 0 && i < total) {
      const s = steps[i];
      if (!s.target) return i;
      if (s.action) return i;
      if (measureTarget(s.target, s.padding)) return i;
      i += dir;
    }
    return -1;
  }, [steps, total]);

  const reposition = useCallback(() => {
    if (!step) return;
    const nextRect = measureTarget(step.target, step.padding);
    setRect(nextRect);
    setPos(computeTooltip(nextRect, step.placement, readTooltipSize()));
  }, [step]);

  const finish = useCallback((completed) => {
    setRunning(false);
    if (completed) markTourDone(storageKey);
    onFinish?.(completed);
  }, [storageKey, onFinish]);

  const skipInDirection = useCallback(() => {
    const dir = dirRef.current;
    const nextIdx = resolveIndex(index + dir, dir);
    if (nextIdx === -1) finish(dir > 0);
    else setIndex(nextIdx);
  }, [index, resolveIndex, finish]);

  // Al entrar a un paso: ejecutar su acción (abrir/cerrar off-canvas), esperar a que
  // aparezca el objetivo (si es asíncrono), llevarlo a la vista y posicionar el globo.
  useLayoutEffect(() => {
    if (!running || !step) return undefined;
    let cancelled = false;
    let rafId = 0;
    const timers = [];

    // Paso 1. Acción del paso -efecto secundario-. Si devuelve false, saltamos el paso.
    if (step.action) {
      let result;
      try { result = step.action(); } catch (err) { result = undefined; }
      if (result === false) {
        skipInDirection();
        return () => { cancelled = true; };
      }
    }

    const positionNow = () => {
      if (cancelled) return;
      const r = measureTarget(step.target, step.padding);
      setRect(r);
      setPos(computeTooltip(r, step.placement, readTooltipSize()));
    };

    const scrollAndSettle = () => {
      if (cancelled) return;
      const el = safeQuery(step.target);
      // Solo scrolleamos si el objetivo no está ya visible, y con el mínimo movimiento.
      if (el && el.scrollIntoView && !step.noScroll && needsScroll(step.target)) {
        el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
      }
      positionNow();
      // Reintentos para asentar tras el scroll suave / animación del off-canvas.
      timers.push(setTimeout(positionNow, 200));
      timers.push(setTimeout(positionNow, 450));
      timers.push(setTimeout(positionNow, 700));
    };

    if (!step.target) {
      positionNow(); // paso centrado
    } else if (safeQuery(step.target)) {
      scrollAndSettle(); // objetivo ya presente
    } else {
      // Objetivo asíncrono (p.ej. el off-canvas abriéndose): lo esperamos.
      setRect(null);
      setPos(computeTooltip(null, step.placement, readTooltipSize()));
      const startedAt = Date.now();
      const timeout = step.waitTimeout ?? DEFAULT_WAIT_TIMEOUT;
      const poll = () => {
        if (cancelled) return;
        if (safeQuery(step.target)) { scrollAndSettle(); return; }
        if (Date.now() - startedAt > timeout) { skipInDirection(); return; }
        rafId = requestAnimationFrame(poll);
      };
      rafId = requestAnimationFrame(poll);
    }

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      timers.forEach(clearTimeout);
    };
  }, [running, index, step, skipInDirection]);

  // Recolocar al hacer scroll (captura scrolls internos) o redimensionar.
  useEffect(() => {
    if (!running) return undefined;
    const onMove = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(reposition);
    };
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
      cancelAnimationFrame(rafRef.current);
    };
  }, [running, reposition]);

  const goNext = useCallback(() => {
    dirRef.current = 1;
    const next = resolveIndex(index + 1, 1);
    if (next === -1) finish(true);
    else setIndex(next);
  }, [index, resolveIndex, finish]);

  const goPrev = useCallback(() => {
    dirRef.current = -1;
    const prev = resolveIndex(index - 1, -1);
    if (prev !== -1) setIndex(prev);
  }, [index, resolveIndex]);

  // Teclado: Esc para salir, → / Enter para avanzar, ← para retroceder.
  useEffect(() => {
    if (!running) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); finish(false); }
      else if (e.key === "ArrowRight" || e.key === "Enter") { e.preventDefault(); goNext(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); goPrev(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [running, finish, goNext, goPrev]);

  // Bloquea el scroll del body mientras dura el recorrido.
  useEffect(() => {
    if (!running) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [running]);

  if (!running || !step) return null;

  const isFirst = resolveIndex(index - 1, -1) === -1;
  const isLast = resolveIndex(index + 1, 1) === -1;

  const content = (
    <div className="gt-root" role="dialog" aria-modal="true" aria-label="Recorrido guiado">
      {/* Capa que bloquea toda interacción con la interfaz */}
      <div className="gt-blocker" />

      {/* Resaltado (solo visual). Si no hay objetivo, oscurecemos toda la pantalla */}
      {rect ? (
        <div className="gt-spotlight" style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }} />
      ) : (
        <div className="gt-dim" />
      )}

      {/* Globo de ayuda */}
      <div
        key={index}
        ref={tooltipRef}
        className={`gt-tooltip gt-tooltip--${pos.placement}`}
        style={{ top: pos.top, left: pos.left }}
      >
        {pos.arrow && <span className={`gt-arrow gt-arrow--${pos.arrow.side}`} style={arrowStyle(pos.arrow)} />}

        <div className="gt-tooltip-head">
          <span className="gt-step-count">Paso {index + 1} de {total}</span>
          <button className="gt-close" onClick={() => finish(false)} aria-label="Cerrar recorrido"><TIcon.Close /></button>
        </div>

        {step.title && <h4 className="gt-title">{step.title}</h4>}
        {step.content && <p className="gt-text">{step.content}</p>}

        <div className="gt-progress">
          {steps.map((_, i) => <span key={i} className={`gt-dot ${i === index ? "gt-dot--active" : ""}`} />)}
        </div>

        <div className="gt-actions">
          <button className="gt-btn-skip" onClick={() => finish(false)}>Saltar</button>
          <div className="gt-actions-right">
            {!isFirst && <button className="gt-btn-ghost" onClick={goPrev}>Anterior</button>}
            <button className="gt-btn-primary" onClick={goNext}>{isLast ? "Finalizar" : "Siguiente"}</button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}